import type { AuthenticatedUser } from '../../types/domain'
import type {
  PaginatedRequisicoes,
  RequisicaoDetail,
  RequisicaoDraft,
  RequisicaoItemRow,
  RequisicaoListItem,
  RequisicaoRow,
  RequisicaoFilters,
} from '../../features/requisicoes/types'
import type { BuyerChange } from '../../features/compras/types'
import { draftItemsToJson } from '../../features/requisicoes/mappers/requisicao.mapper'
import { offlineDb, scopedKey } from './db'
import type { CacheMetadata, CachedRequisition } from './types'
import type { Json } from '../supabase/database.types'
import type { Tables } from '../supabase/database.types'

function metadata(entry: CachedRequisition, source: 'server' | 'cache'): CacheMetadata {
  return {
    source,
    cachedAt: entry.cachedAt,
    serverVersion: entry.serverVersion,
    pending: entry.pending,
    localOnly: entry.localOnly,
    locallyDeleted: entry.locallyDeleted,
  }
}

export async function cacheProfile(user: AuthenticatedUser): Promise<void> {
  const db = await offlineDb()
  await db.put('sync_metadata', {
    key: scopedKey(user.userId, 'profile'),
    userId: user.userId,
    kind: 'profile',
    value: user as unknown as Json,
    updatedAt: new Date().toISOString(),
  })
}

export async function getCachedProfile(userId: string): Promise<AuthenticatedUser | null> {
  const db = await offlineDb()
  const entry = await db.get('sync_metadata', scopedKey(userId, 'profile'))
  return entry?.kind === 'profile' ? entry.value as unknown as AuthenticatedUser : null
}

export async function cacheCatalog(userId: string, rows: Tables<'itens_catalogo'>[]): Promise<void> {
  const db = await offlineDb()
  const now = new Date().toISOString()
  const tx = db.transaction('cache_catalogo_itens', 'readwrite')
  for (const row of rows) {
    await tx.store.put({ key: scopedKey(userId, row.id), userId, row, cachedAt: now, serverVersion: row.versao })
  }
  await tx.done
}

export async function searchCachedCatalog(userId: string, query: string, limit = 12): Promise<Tables<'itens_catalogo'>[]> {
  const db = await offlineDb()
  const rows = await db.getAllFromIndex('cache_catalogo_itens', 'by_user', userId)
  const normalized = query.trim().toLocaleLowerCase('pt-BR')
  return rows.map((entry) => entry.row).filter((row) => row.ativo && !row.deleted_at && (
    row.codigo.toLocaleLowerCase('pt-BR').includes(normalized)
    || row.descricao.toLocaleLowerCase('pt-BR').includes(normalized)
    || row.categoria?.toLocaleLowerCase('pt-BR').includes(normalized)
  )).sort((a, b) => a.codigo.localeCompare(b.codigo)).slice(0, limit)
}

export async function cacheList(userId: string, rows: RequisicaoListItem[]): Promise<void> {
  const db = await offlineDb()
  const now = new Date().toISOString()
  const tx = db.transaction(['cache_requisicoes', 'cache_itens', 'sync_metadata'], 'readwrite')
  for (const { itens, ...row } of rows) {
    const current = await tx.objectStore('cache_requisicoes').get(scopedKey(userId, row.id))
    await tx.objectStore('cache_requisicoes').put({
      key: scopedKey(userId, row.id), userId, row, cachedAt: now,
      serverVersion: row.versao, pending: current?.pending ?? false,
      localOnly: current?.localOnly ?? false, locallyDeleted: false, listItems: itens,
    })
    for (const item of itens) {
      const cachedItem = await tx.objectStore('cache_itens').get(scopedKey(userId, item.id))
      if (cachedItem) {
        await tx.objectStore('cache_itens').put({ ...cachedItem, cachedAt: now })
      }
    }
  }
  await tx.objectStore('sync_metadata').put({
    key: scopedKey(userId, 'last_sync'), userId, kind: 'last_sync', value: now, updatedAt: now,
  })
  await tx.done
}

export async function cacheDetail(userId: string, detail: RequisicaoDetail): Promise<RequisicaoDetail> {
  const db = await offlineDb()
  const now = new Date().toISOString()
  const tx = db.transaction(['cache_requisicoes', 'cache_itens', 'cache_historico', 'sync_metadata'], 'readwrite')
  const requestKey = scopedKey(userId, detail.requisicao.id)
  await tx.objectStore('cache_requisicoes').put({
    key: requestKey, userId, row: detail.requisicao, cachedAt: now,
    serverVersion: detail.requisicao.versao, pending: false, localOnly: false, locallyDeleted: false,
    listItems: detail.itens.map((row) => ({
      id: row.id, codigo: row.codigo, descricao: row.descricao, prioridade: row.prioridade,
      status_item: row.status_item, quantidade_comprada: row.quantidade_comprada, fornecedor: row.fornecedor,
    })),
  })

  const oldItems = await tx.objectStore('cache_itens').index('by_user_requisicao').getAll([userId, detail.requisicao.id])
  for (const item of oldItems) await tx.objectStore('cache_itens').delete(item.key)
  for (const row of detail.itens) {
    await tx.objectStore('cache_itens').put({
      key: scopedKey(userId, row.id), userId, requisicaoId: detail.requisicao.id, row,
      cachedAt: now, serverVersion: row.versao, pending: false, locallyDeleted: false,
    })
  }

  const oldHistory = await tx.objectStore('cache_historico').index('by_user_requisicao').getAll([userId, detail.requisicao.id])
  for (const entry of oldHistory) await tx.objectStore('cache_historico').delete(entry.key)
  for (const row of detail.historico) {
    await tx.objectStore('cache_historico').put({
      key: scopedKey(userId, row.id), userId, requisicaoId: detail.requisicao.id, row, cachedAt: now,
    })
  }
  await tx.objectStore('sync_metadata').put({
    key: scopedKey(userId, 'last_sync'), userId, kind: 'last_sync', value: now, updatedAt: now,
  })
  await tx.done
  return { ...detail, offline: { source: 'server', cachedAt: now, serverVersion: detail.requisicao.versao } }
}

export async function getCachedDetail(userId: string, requisicaoId: string): Promise<RequisicaoDetail | null> {
  const db = await offlineDb()
  const request = await db.get('cache_requisicoes', scopedKey(userId, requisicaoId))
  if (!request || request.locallyDeleted) return null
  const [items, history] = await Promise.all([
    db.getAllFromIndex('cache_itens', 'by_user_requisicao', [userId, requisicaoId]),
    db.getAllFromIndex('cache_historico', 'by_user_requisicao', [userId, requisicaoId]),
  ])
  return {
    requisicao: { ...request.row, offline: metadata(request, 'cache') },
    itens: items.filter((entry) => !entry.locallyDeleted).map((entry) => ({
      ...entry.row,
      offline: { source: 'cache', cachedAt: entry.cachedAt, serverVersion: entry.serverVersion, pending: entry.pending },
    })),
    historico: history.map((entry) => entry.row).sort((a, b) => b.created_at.localeCompare(a.created_at)),
    offline: metadata(request, 'cache'),
  }
}

export async function getCachedList(userId: string, filters: RequisicaoFilters): Promise<PaginatedRequisicoes> {
  const db = await offlineDb()
  const requests = await db.getAllFromIndex('cache_requisicoes', 'by_user', userId)
  const items = await db.getAllFromIndex('cache_itens', 'by_user', userId)
  const search = filters.search.trim().toLocaleLowerCase('pt-BR')
  const mapped = requests.filter((entry) => !entry.locallyDeleted).map((entry): RequisicaoListItem => {
    const requestItems = items.filter((item) => item.requisicaoId === entry.row.id && !item.locallyDeleted)
    return {
      ...entry.row,
      offline: metadata(entry, 'cache'),
      itens: (requestItems.length ? requestItems.map(({ row }) => ({
        id: row.id, codigo: row.codigo, descricao: row.descricao, prioridade: row.prioridade,
        status_item: row.status_item, quantidade_comprada: row.quantidade_comprada, fornecedor: row.fornecedor,
      })) : entry.listItems),
    }
  }).filter((entry) => {
    if (filters.status && entry.status_geral !== filters.status) return false
    if (filters.itemStatus && !entry.itens.some((item) => item.status_item === filters.itemStatus)) return false
    if (filters.bloqueada !== 'todas' && entry.bloqueada !== (filters.bloqueada === 'bloqueadas')) return false
    if (!search) return true
    return [entry.numero_requisicao, entry.obra, entry.setor, entry.requisitante_nome,
      ...entry.itens.flatMap((item) => [item.codigo, item.descricao])]
      .some((value) => value?.toLocaleLowerCase('pt-BR').includes(search))
  }).sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  const from = filters.page * filters.pageSize
  return { data: mapped.slice(from, from + filters.pageSize), count: mapped.length, page: filters.page, pageSize: filters.pageSize, offline: true }
}

export async function createLocalRequisition(user: AuthenticatedUser, localId: string, draft: RequisicaoDraft): Promise<RequisicaoRow> {
  const now = new Date().toISOString()
  const row: RequisicaoRow = {
    id: localId,
    numero_requisicao: `RASCUNHO-OFFLINE-${localId.slice(0, 4).toUpperCase()}`,
    data_requisicao: draft.data_requisicao,
    obra: draft.obra.trim() || null,
    setor: draft.setor.trim() || null,
    requisitante_id: user.userId,
    requisitante_nome: user.profile.nome,
    status_geral: 'Nova Requisição',
    justificativa: draft.justificativa.trim() || null,
    solicitante_nome: draft.solicitante_nome.trim() || null,
    responsavel_nome: draft.responsavel_nome.trim() || null,
    bloqueada: false,
    versao: 1,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    deleted_by: null,
    offline: { source: 'cache', cachedAt: now, serverVersion: 0, pending: true, localOnly: true },
  }
  const parsedItems = draftItemsToJson(draft.itens) as Array<Record<string, unknown>>
  const itens: RequisicaoItemRow[] = draft.itens.map((draftItem, index) => ({
    id: draftItem.id, requisicao_id: localId,
    codigo: stringOrNull(parsedItems[index]?.codigo), descricao: String(parsedItems[index]?.descricao ?? ''),
    grade1: stringOrNull(parsedItems[index]?.grade1), grade2: stringOrNull(parsedItems[index]?.grade2),
    grade3: stringOrNull(parsedItems[index]?.grade3), unidade: stringOrNull(parsedItems[index]?.unidade),
    quantidade_solicitada: Number(parsedItems[index]?.quantidade_solicitada),
    necessidade: stringOrNull(parsedItems[index]?.necessidade), prioridade: stringOrNull(parsedItems[index]?.prioridade),
    quantidade_comprada: null, fornecedor: null, prazo_entrega: null, status_item: 'Aguardando',
    observacao_comprador: null, versao: 1, created_at: now, updated_at: now, deleted_at: null, deleted_by: null,
    item_catalogo_id: draftItem.item_catalogo_id,
    offline: { source: 'cache', cachedAt: now, serverVersion: 0, pending: true, localOnly: true },
  }))
  await putLocalDetail(user.userId, { requisicao: row, itens, historico: [], offline: row.offline })
  return row
}

export async function putLocalDetail(userId: string, detail: RequisicaoDetail): Promise<void> {
  const db = await offlineDb()
  const now = new Date().toISOString()
  const tx = db.transaction(['cache_requisicoes', 'cache_itens'], 'readwrite')
  const current = await tx.objectStore('cache_requisicoes').get(scopedKey(userId, detail.requisicao.id))
  await tx.objectStore('cache_requisicoes').put({
    key: scopedKey(userId, detail.requisicao.id), userId, row: detail.requisicao, cachedAt: now,
    serverVersion: current?.serverVersion ?? detail.requisicao.versao,
    pending: true, localOnly: current?.localOnly ?? false, locallyDeleted: false,
    listItems: detail.itens.map((row) => ({
      id: row.id, codigo: row.codigo, descricao: row.descricao, prioridade: row.prioridade,
      status_item: row.status_item, quantidade_comprada: row.quantidade_comprada, fornecedor: row.fornecedor,
    })),
  })
  const activeItemIds = new Set(detail.itens.map((item) => item.id))
  const oldItems = await tx.objectStore('cache_itens').index('by_user_requisicao').getAll([userId, detail.requisicao.id])
  for (const item of oldItems) {
    if (!activeItemIds.has(item.row.id)) await tx.objectStore('cache_itens').put({ ...item, pending: true, locallyDeleted: true })
  }
  for (const row of detail.itens) {
    const currentItem = await tx.objectStore('cache_itens').get(scopedKey(userId, row.id))
    await tx.objectStore('cache_itens').put({
      key: scopedKey(userId, row.id), userId, requisicaoId: detail.requisicao.id, row, cachedAt: now,
      serverVersion: currentItem?.serverVersion ?? row.versao, pending: true, locallyDeleted: false,
    })
  }
  await tx.done
}

export async function applyRequesterDraft(userId: string, draft: RequisicaoDraft): Promise<RequisicaoRow> {
  if (!draft.id) throw new Error('Requisição local sem identificador.')
  const current = await getCachedDetail(userId, draft.id)
  if (!current) throw new Error('Este registro não está disponível offline.')
  const now = new Date().toISOString()
  const byId = new Map(current.itens.map((item) => [item.id, item]))
  const itens: RequisicaoItemRow[] = draft.itens.map((item) => {
    const persisted = byId.get(item.id)
    return {
      id: item.id,
      requisicao_id: draft.id as string,
      codigo: textOrNull(item.codigo),
      descricao: item.descricao.trim(),
      grade1: textOrNull(item.grade1),
      grade2: textOrNull(item.grade2),
      grade3: textOrNull(item.grade3),
      unidade: textOrNull(item.unidade),
      quantidade_solicitada: Number(item.quantidade_solicitada.replace(',', '.')),
      necessidade: textOrNull(item.necessidade),
      prioridade: textOrNull(item.prioridade),
      quantidade_comprada: persisted?.quantidade_comprada ?? null,
      fornecedor: persisted?.fornecedor ?? null,
      prazo_entrega: persisted?.prazo_entrega ?? null,
      status_item: persisted?.status_item ?? 'Aguardando',
      observacao_comprador: persisted?.observacao_comprador ?? null,
      versao: persisted?.versao ?? 1,
      created_at: persisted?.created_at ?? now,
      updated_at: now,
      deleted_at: null,
      deleted_by: null,
      item_catalogo_id: item.item_catalogo_id,
      offline: { source: 'cache', cachedAt: now, serverVersion: persisted?.versao ?? 0, pending: true },
    }
  })
  const requisicao: RequisicaoRow = {
    ...current.requisicao,
    data_requisicao: draft.data_requisicao,
    obra: textOrNull(draft.obra),
    setor: textOrNull(draft.setor),
    justificativa: textOrNull(draft.justificativa),
    solicitante_nome: textOrNull(draft.solicitante_nome),
    responsavel_nome: textOrNull(draft.responsavel_nome),
    updated_at: now,
    offline: { source: 'cache', cachedAt: now, serverVersion: draft.versao ?? 0, pending: true },
  }
  await putLocalDetail(userId, { requisicao, itens, historico: current.historico, offline: requisicao.offline })
  return requisicao
}

export async function applyBuyerChanges(userId: string, requisicaoId: string, changes: BuyerChange[]): Promise<RequisicaoDetail> {
  const current = await getCachedDetail(userId, requisicaoId)
  if (!current) throw new Error('Este registro não está disponível offline.')
  const now = new Date().toISOString()
  const byId = new Map(changes.map((change) => [change.id, change]))
  const itens = current.itens.map((item): RequisicaoItemRow => {
    const change = byId.get(item.id)
    if (!change) return item
    return {
      ...item,
      quantidade_comprada: change.quantidade_comprada,
      fornecedor: change.fornecedor,
      prazo_entrega: change.prazo_entrega,
      status_item: change.status_item,
      observacao_comprador: change.observacao_comprador,
      updated_at: now,
      offline: { source: 'cache', cachedAt: now, serverVersion: item.versao, pending: true },
    }
  })
  const detail: RequisicaoDetail = {
    requisicao: { ...current.requisicao, offline: { source: 'cache', cachedAt: now, serverVersion: current.requisicao.versao, pending: true } },
    itens,
    historico: current.historico,
    offline: { source: 'cache', cachedAt: now, serverVersion: current.requisicao.versao, pending: true },
  }
  await putLocalDetail(userId, detail)
  return detail
}

export async function markLocallyBlocked(userId: string, requisicaoId: string): Promise<RequisicaoRow> {
  const current = await getCachedDetail(userId, requisicaoId)
  if (!current) throw new Error('Este registro não está disponível offline.')
  const row = { ...current.requisicao, bloqueada: true, updated_at: new Date().toISOString() }
  await putLocalDetail(userId, { ...current, requisicao: row, offline: { ...current.offline!, pending: true } })
  return row
}

export async function markLocallyDeleted(userId: string, requisicaoId: string): Promise<void> {
  const db = await offlineDb()
  const key = scopedKey(userId, requisicaoId)
  const entry = await db.get('cache_requisicoes', key)
  if (entry) await db.put('cache_requisicoes', { ...entry, pending: true, locallyDeleted: true })
}

export async function getLastSync(userId: string): Promise<string | null> {
  const db = await offlineDb()
  const entry = await db.get('sync_metadata', scopedKey(userId, 'last_sync'))
  return entry?.kind === 'last_sync' && typeof entry.value === 'string' ? entry.value : null
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed || null
}
